import React, { forwardRef, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import Image from 'components/image';
import TextSkeleton from 'components/text-skeleton';
import RecipeDetails, {
  sizes as recipeDetailsSizes,
} from 'components/recipe-details';
import orientations from './recipe-teaser-orientations';
import idHelper from 'js/id-helper';
import { imageSourceBreakpoints } from 'js/breakpoints';

// eslint-disable-next-line view-models/no-errors
export const places = {
  recipeTeasersBlock: 'recipe-teasers-block',
  recipeList: 'recipe-list',
  searchPage: 'search-page',
};

const getImageSources = teaserPlace => {
  switch (teaserPlace) {
    case places.recipeTeasersBlock:
      return [
        { width: 290, height: 186, media: imageSourceBreakpoints.xs },
        { width: 552, height: 354, media: imageSourceBreakpoints.s },
        { width: 695, height: 446, media: imageSourceBreakpoints.m },
        { width: 312, height: 200, media: imageSourceBreakpoints.l },
      ];
    case places.recipeList:
    case places.searchPage:
      return [
        { width: 190, height: 206, media: imageSourceBreakpoints.xs },
        { width: 348, height: 224, media: imageSourceBreakpoints.s },
        { width: 286, height: 183, media: imageSourceBreakpoints.m },
        { width: 312, height: 200, media: imageSourceBreakpoints.l },
      ];

    default:
      throw new Error(
        `Unsupported recipe teaser place: "${teaserPlace}". Valid places are: ${Object.values(places).join(', ')}`
      );
  }
};

const RecipeTeaser = forwardRef(
  (
    {
      heading,
      url,
      image,
      className,
      rating,
      cookingTime,
      difficultyLevel,
      tabindex,
      ariaRoledescription,
      mobileOrientation = orientations.vertical,
      headingTagLevel = 3,
      cookingTimeIconAriaLabel,
      difficultyLevelIconAriaLabel,
      ratingIconAriaLabel,
      preloadImage,
      place,
      asSkeleton,
    },
    ref
  ) => {
    const imageHolderRef = useRef(null);
    const isHorizontalOrientationOnMobile =
      mobileOrientation === orientations.horizontal;
    const HeadingTag = `h${headingTagLevel}`;

    const uniqueRecipeTeaserHeadingId = useMemo(
      () => idHelper.generateUniqueId('recipe-teaser-heading'),
      []
    );

    return (
      <a
        ref={ref}
        href={url}
        className={cn('recipe-teaser', {
          [className]: className,
          'recipe-teaser_horizontal-mobile': isHorizontalOrientationOnMobile,
          'recipe-teaser_as-skeleton': asSkeleton,
        })}
        tabIndex={tabindex ? tabindex : null}
        aria-labelledby={uniqueRecipeTeaserHeadingId}
        aria-roledescription={ariaRoledescription ? ariaRoledescription : null}
      >
        <div
          className={cn('recipe-teaser__image-holder', {
            'recipe-teaser__image-holder_no-image': !image,
          })}
          ref={imageHolderRef}
        >
          {image && (
            <Image
              {...image}
              sources={getImageSources(place)}
              {...(!preloadImage && { loading: 'lazy' })}
              {...(preloadImage && { fetchPriority: 'high' })}
              className="recipe-teaser__image image_size_recipe-teaser"
            />
          )}
        </div>
        <div className="recipe-teaser__content">
          <div className="recipe-teaser__heading-holder">
            {asSkeleton ? (
              <TextSkeleton
                className="recipe-teaser__heading-skeleton"
                lineClassName="recipe-teaser__heading-skeleton-line"
                maxLines={4}
              />
            ) : (
              heading && (
                <HeadingTag
                  className="recipe-teaser__heading"
                  id={uniqueRecipeTeaserHeadingId}
                >
                  {heading}
                </HeadingTag>
              )
            )}
          </div>
          <RecipeDetails
            size={recipeDetailsSizes.small}
            className="recipe-teaser__details"
            cookingTime={cookingTime}
            difficultyLevel={difficultyLevel}
            rating={rating}
            cookingTimeIconAriaLabel={cookingTimeIconAriaLabel}
            difficultyLevelIconAriaLabel={difficultyLevelIconAriaLabel}
            ratingIconAriaLabel={ratingIconAriaLabel}
            asSkeleton={asSkeleton}
          />
        </div>
      </a>
    );
  }
);

RecipeTeaser.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  heading: PropTypes.string,
  image: PropTypes.exact(Image.propTypes),
  url: PropTypes.string,
  rating: PropTypes.string,
  cookingTime: PropTypes.string,
  difficultyLevel: PropTypes.string,
  tabindex: PropTypes.number,
  ariaRoledescription: PropTypes.string,
  place: PropTypes.oneOf(Object.values(places)),
  mobileOrientation: PropTypes.oneOf(Object.values(orientations)),
  headingTagLevel: PropTypes.number,
  cookingTimeIconAriaLabel: PropTypes.string,
  difficultyLevelIconAriaLabel: PropTypes.string,
  ratingIconAriaLabel: PropTypes.string,
  preloadImage: PropTypes.bool,
  asSkeleton: PropTypes.bool,
};

RecipeTeaser.viewModelMeta = {
  className: 'ignore',
  tabindex: 'ignore',
  ariaRoledescription: 'ignore',
  mobileOrientation: 'ignore',
  headingTagLevel: 'ignore',
  preloadImage: 'ignore',
  place: 'ignore',
  asSkeleton: 'ignore',
};

// eslint-disable-next-line view-models/no-errors
export default RecipeTeaser;
